// src/engines/workflow/validateWorkflowTransition.js

/**
 * =====================================================
 * WORKFLOW TRANSITION VALIDATOR
 * =====================================================
 *
 * PURPOSE:
 * - Single gate for all workflow state transitions
 * - Prevents illegal state mutations
 * - Enforces terminal state protection
 * - Isolates DIRECT / ACCEPTANCE / RETURN engines
 *
 * RULES:
 * - Call this BEFORE every Firestore workflow update
 * - Never bypass this for any reason
 * - Never add transition logic outside this file
 * - All new workflows must be registered here
 *
 * USED BY:
 * - assetTransactionService.js
 *   → approveTransfer
 *   → rejectTransfer
 *   → cancelTransfer
 *   → receiveReturnedAsset
 *
 * TRANSITION MAP (approved flows only):
 * ─────────────────────────────────────────────────────
 * DIRECT_FINAL / TRANSFER
 *   (no transitions — immediately FINALIZED on create)
 *
 * ACCEPTANCE_REQUIRED / TRANSFER
 *   PENDING_ACCEPTANCE → FINALIZED   (receiver accepts)
 *   PENDING_ACCEPTANCE → REJECTED    (receiver rejects)
 *   PENDING_ACCEPTANCE → CANCELLED   (requester cancels)
 *
 * RETURN_APPROVAL / RETURN
 *   RETURN_PENDING → FINALIZED       (store receives)
 *   RETURN_PENDING → REJECTED        (store rejects)
 *   RETURN_PENDING → CANCELLED       (requester cancels)
 * ─────────────────────────────────────────────────────
 *
 * =====================================================
 */

import { WORKFLOW_TYPES } from "../../services/assets/workflowTypes";
import { WORKFLOW_STATES, TERMINAL_WORKFLOW_STATES } from "../../services/assets/workflowStates";
import { TRANSACTION_CLASSES } from "../../services/assets/transactionClasses";

// =====================================================
// LEGAL TRANSITION MAP
//
// Structure:
// WORKFLOW_TYPE → TRANSACTION_CLASS → fromState → [allowed toStates]
//
// Any transition NOT listed here is ILLEGAL.
// =====================================================

const LEGAL_TRANSITIONS = Object.freeze({

    // ─────────────────────────────────────────────
    // DIRECT FINAL
    // No post-create transitions allowed.
    // Immediately FINALIZED on createTransferRequest.
    // Included here for completeness + replay safety.
    // ─────────────────────────────────────────────

    [WORKFLOW_TYPES.DIRECT_FINAL]: {
        [TRANSACTION_CLASSES.TRANSFER]: {
            // No active states — always created as FINALIZED
            // No transitions registered intentionally
        },
    },

    // ─────────────────────────────────────────────
    // ACCEPTANCE REQUIRED
    // Flows: STAFF→STAFF, SITE→STAFF, STAFF→SITE
    // ─────────────────────────────────────────────

    [WORKFLOW_TYPES.ACCEPTANCE_REQUIRED]: {
        [TRANSACTION_CLASSES.TRANSFER]: {

            [WORKFLOW_STATES.PENDING_ACCEPTANCE]: [
                WORKFLOW_STATES.FINALIZED,   // receiver accepts → custody transfers
                WORKFLOW_STATES.REJECTED,    // receiver rejects → custody unchanged
                WORKFLOW_STATES.CANCELLED,   // requester cancels → custody unchanged
            ],
        },
    },

    // ─────────────────────────────────────────────
    // RETURN APPROVAL
    // Flows: STAFF→STORE, SITE→STORE
    // ─────────────────────────────────────────────

    [WORKFLOW_TYPES.RETURN_APPROVAL]: {
        [TRANSACTION_CLASSES.RETURN]: {

            [WORKFLOW_STATES.RETURN_PENDING]: [
                WORKFLOW_STATES.FINALIZED,   // store receives asset → holder → STORE
                WORKFLOW_STATES.REJECTED,    // store rejects return → holder unchanged
                WORKFLOW_STATES.CANCELLED,   // requester cancels return → holder unchanged
            ],
        },
    },

});

// =====================================================
// CORE VALIDATOR
// =====================================================

/**
 * validateWorkflowTransition
 *
 * @param {Object} params
 * @param {string} params.workflowType        - WORKFLOW_TYPES.*
 * @param {string} params.transactionClass    - TRANSACTION_CLASSES.*
 * @param {string} params.fromState           - current WORKFLOW_STATES.*
 * @param {string} params.toState             - target WORKFLOW_STATES.*
 *
 * @returns {{ valid: boolean, reason: string | null }}
 */

export function validateWorkflowTransition({
    workflowType,
    transactionClass,
    fromState,
    toState,
}) {

    // ─────────────────────────────────────────
    // 1. Input presence
    // ─────────────────────────────────────────

    if (!workflowType) {
        return _fail("workflowType is required");
    }

    if (!transactionClass) {
        return _fail("transactionClass is required");
    }

    if (!fromState) {
        return _fail("fromState is required");
    }

    if (!toState) {
        return _fail("toState is required");
    }

    // ─────────────────────────────────────────
    // 2. Self-transition block
    // ─────────────────────────────────────────

    if (fromState === toState) {
        return _fail(
            `Self-transition not allowed: ${fromState} → ${toState}`
        );
    }

    // ─────────────────────────────────────────
    // 3. Terminal state block
    // Once FINALIZED / REJECTED / CANCELLED
    // → transaction is closed forever
    // ─────────────────────────────────────────

    if (TERMINAL_WORKFLOW_STATES.includes(fromState)) {
        return _fail(
            `Cannot transition from terminal state '${fromState}'. ` +
            `Transaction is permanently closed.`
        );
    }

    // ─────────────────────────────────────────
    // 4. Workflow type existence check
    // ─────────────────────────────────────────

    const workflowMap = LEGAL_TRANSITIONS[workflowType];

    if (!workflowMap) {
        return _fail(
            `Unknown workflowType: '${workflowType}'`
        );
    }

    // ─────────────────────────────────────────
    // 5. Transaction class existence check
    // ─────────────────────────────────────────

    const classMap = workflowMap[transactionClass];

    if (!classMap) {
        return _fail(
            `workflowType '${workflowType}' does not support ` +
            `transactionClass '${transactionClass}'`
        );
    }

    // ─────────────────────────────────────────
    // 6. DIRECT_FINAL special block
    // These are created as FINALIZED — no further
    // transitions are ever valid
    // ─────────────────────────────────────────

    if (workflowType === WORKFLOW_TYPES.DIRECT_FINAL) {
        return _fail(
            `DIRECT_FINAL transactions are created as FINALIZED. ` +
            `No post-create transitions are permitted.`
        );
    }

    // ─────────────────────────────────────────
    // 7. fromState existence check
    // ─────────────────────────────────────────

    const allowedTargets = classMap[fromState];

    if (!allowedTargets) {
        return _fail(
            `No transitions defined from state '${fromState}' ` +
            `for workflow '${workflowType}/${transactionClass}'`
        );
    }

    // ─────────────────────────────────────────
    // 8. toState legality check
    // ─────────────────────────────────────────

    if (!allowedTargets.includes(toState)) {
        return _fail(
            `Illegal transition: '${fromState}' → '${toState}' ` +
            `in workflow '${workflowType}/${transactionClass}'. ` +
            `Allowed targets: [${allowedTargets.join(", ")}]`
        );
    }

    // ─────────────────────────────────────────
    // 9. All checks passed
    // ─────────────────────────────────────────

    return _pass();
}

// =====================================================
// HELPER — Get allowed next states
// Use in UI to show only valid action buttons
// =====================================================

/**
 * getAllowedTransitions
 *
 * @param {string} workflowType
 * @param {string} transactionClass
 * @param {string} fromState
 * @returns {string[]} - legal toStates, empty array if none
 */

export function getAllowedTransitions(
    workflowType,
    transactionClass,
    fromState
) {
    return (
        LEGAL_TRANSITIONS[workflowType]
            ?.[transactionClass]
            ?.[fromState]
        ?? []
    );
}

// =====================================================
// HELPER — Is state terminal
// =====================================================

/**
 * isTerminalState
 *
 * @param {string} state
 * @returns {boolean}
 */

export function isTerminalState(state) {
    return TERMINAL_WORKFLOW_STATES.includes(state);
}

// =====================================================
// INTERNAL RESULT FACTORIES
// =====================================================

function _pass() {
    return { valid: true, reason: null };
}

function _fail(reason) {
    return { valid: false, reason };
}